import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  notificationCountGateway,
  type NotificationCountGateway,
} from '../notifications/notification-count-gateway';
import { useSession } from '../session/SessionContext';
import {
  subscribeToRows,
  tableRowsChannel,
  type RealtimeMessage,
  type Unsubscribe,
} from '../../services/appwrite/realtime';
import { useToast } from '../../shared/accessibility/Toast';
import { useAudio } from '../../shared/audio/AudioProvider';
import { playSiteSound, prepareSiteSound } from './notification-sounds';

const NOTIFICATION_SOUND = 'notification.received';

/**
 * Canales base que toda sesión mantiene activos. Las acciones de cuenta se
 * modelan como notificaciones con `kind` y `actions`, para no abrir un canal
 * distinto por cada caso de uso.
 */
const CORE_CHANNELS = [
  tableRowsChannel('principal', 'notifications'),
  tableRowsChannel('principal', 'direct_messages'),
];

interface AccountEvent {
  $id?: string;
  userId?: string;
  text?: string;
  recipientUserId?: string;
  senderDisplayName?: string;
  conversationId?: string;
  kind?: string;
}

export type RealtimeSubscribe = <T>(
  channels: string[],
  onMessage: (message: RealtimeMessage<T>) => void,
) => Unsubscribe;

interface RealtimeSyncProps {
  /** Suscripción alternativa para pruebas. */
  subscribe?: RealtimeSubscribe;
  /** Gateway alternativo para pruebas; `null` omite la comprobación de respaldo. */
  notificationGateway?: NotificationCountGateway | null;
}

/**
 * Abre la conexión Realtime al autenticar y la conserva durante toda la
 * sesión, con independencia del dominio o la ruta actual.
 *
 * Los módulos que solo existen mientras se usan (chat, games) agregan sus
 * pocos canales amplios a la misma instancia compartida; nunca crean otro
 * WebSocket ni un canal por sala, mensaje o acción.
 */
export function RealtimeSync({ subscribe, notificationGateway: injectedGateway }: RealtimeSyncProps) {
  const { state } = useSession();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const audio = useAudio();
  const receivedNotifications = useRef(new Set<string>());
  const previousCount = useRef<number | null>(null);
  const countGateway = injectedGateway === undefined
    ? (import.meta.env.MODE === 'test' ? null : notificationCountGateway)
    : injectedGateway;
  const notificationCount = useQuery({
    queryKey: ['notification-count'],
    queryFn: () => countGateway?.count() ?? Promise.resolve(0),
    enabled: state.status === 'authenticated' && countGateway !== null,
    // Realtime sigue siendo el camino inmediato. Esto recupera el estado si
    // el navegador suspendió o perdió el WebSocket mientras estaba al fondo.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (state.status !== 'authenticated' || !state.profile.profile.notificationSoundsEnabled) return;
    void prepareSiteSound(audio, NOTIFICATION_SOUND);
  }, [audio, state]);

  useEffect(() => {
    if (state.status !== 'authenticated') {
      previousCount.current = null;
      return;
    }
    const current = notificationCount.data;
    if (current === undefined) return;
    const previous = previousCount.current;
    previousCount.current = current;
    // La primera lectura representa avisos que ya existían antes de abrir la
    // página. Solo suena cuando la cantidad aumenta durante esta sesión.
    if (
      previous !== null
      && current > previous
      && state.profile.profile.notificationSoundsEnabled
    ) {
      void playSiteSound(audio, NOTIFICATION_SOUND);
    }
  }, [audio, notificationCount.data, state]);

  useEffect(() => {
    if (state.status !== 'authenticated') return undefined;
    // Las pruebas generales de páginas no deben abrir sockets contra el
    // servidor real. Esta pieza se prueba inyectando su suscripción.
    const listen = subscribe ?? (import.meta.env.MODE === 'test' ? null : subscribeToRows);
    if (!listen) return undefined;
    const userId = state.profile.user.id;

    return listen<AccountEvent>(CORE_CHANNELS, (event) => {
      const payload = event.payload ?? {};
      const directMessage = Boolean(payload.conversationId && payload.recipientUserId);
      if (directMessage) {
        if (payload.recipientUserId !== userId) return;
        void queryClient.invalidateQueries({ queryKey: ['direct-conversations'] });
        void queryClient.invalidateQueries({ queryKey: ['direct-messages', payload.conversationId] });
        void queryClient.invalidateQueries({ queryKey: ['direct-unread-count'] });
        const created = (event.events ?? []).some((name) => name.endsWith('.create'));
        if (created) {
          if (state.profile.profile.notificationToastsEnabled) {
            showToast({
              title: 'Nuevo mensaje privado',
              description: `${payload.senderDisplayName || 'Alguien'} te envió un mensaje.`,
              tone: 'info',
              timeout: 10_000,
            });
          }
          if (state.profile.profile.notificationSoundsEnabled) void playSiteSound(audio, NOTIFICATION_SOUND);
        }
        return;
      }
      if (payload.userId && payload.userId !== userId) return;

      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      void queryClient.invalidateQueries({ queryKey: ['notification-count'] });
      if (payload.kind === 'activity') {
        void queryClient.invalidateQueries({ queryKey: ['public-activities'] });
        void queryClient.invalidateQueries({ queryKey: ['managed-activities'] });
        void queryClient.invalidateQueries({ queryKey: ['my-activity-registrations'] });
        void queryClient.invalidateQueries({ queryKey: ['activity'] });
        void queryClient.invalidateQueries({ queryKey: ['activity-timeline'] });
      }

      const created = (event.events ?? []).some((name) => name.endsWith('.create'));
      // El aviso en pantalla es igual para toda notificación; quien no lo
      // quiera lo apaga desde su propia preferencia (Configuración), no
      // decidiendo por tipo de notificación — ver SettingsPage/NotificationPreferences.
      if (created && payload.text && state.profile.profile.notificationToastsEnabled) {
        if (payload.$id && receivedNotifications.current.has(payload.$id)) return;
        if (payload.$id) receivedNotifications.current.add(payload.$id);
        showToast({
          title: 'Nueva notificación',
          description: payload.text,
          tone: 'info',
          timeout: 10_000,
        });
      }
    });
  }, [audio, queryClient, showToast, state, subscribe]);

  return null;
}
